import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Form, Row, Col, Button } from 'react-bootstrap';
import type { FormValues } from '../CustomerWizard';

const AddressStep: React.FC<{ defaultAddressType?: 'BUSINESS' | 'RESIDENTIAL' | 'POSTAL' }> = ({ defaultAddressType = 'BUSINESS' }) => {
  const { register, formState: { errors } } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    name: 'addresses'
  });

  const handleAddAddress = () => {
    append({
      type: defaultAddressType,
      line1: '',
      line2: '',
      city: '',
      province: '',
      postalCode: '',
      country: '',
      isPrimary: false
    });
  };

  return (
    <div className="address-step">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Addresses</h4>
        <Button variant="outline-primary" onClick={handleAddAddress}>
          <i className="fa fa-plus me-2"></i>
          Add Address
        </Button>
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">Address {index + 1}</h5>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => remove(index)}
              >
                <i className="fa fa-trash"></i>
              </Button>
            </div>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Address Type</Form.Label>
                  <Form.Select
                    {...register(`addresses.${index}.type`)}
                    className={errors.addresses?.[index]?.type ? 'is-invalid' : ''}
                  >
                    <option value="BUSINESS">Business</option>
                    <option value="POSTAL">Postal</option>
                    <option value="RESIDENTIAL">Residential</option>
                  </Form.Select>
                  {errors.addresses?.[index]?.type && (
                    <Form.Control.Feedback type="invalid">
                      {String(errors.addresses?.[index]?.type)}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Street Address</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`addresses.${index}.line1`)}
                    className={errors.addresses?.[index]?.line1 ? 'is-invalid' : ''}
                  />
                  {errors.addresses?.[index]?.line1 && (
                    <Form.Control.Feedback type="invalid">
                      {String(errors.addresses?.[index]?.line1)}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Suburb</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`addresses.${index}.line2`)}
                    className={errors.addresses?.[index]?.line2 ? 'is-invalid' : ''}
                  />
                  {errors.addresses?.[index]?.line2 && (
                    <Form.Control.Feedback type="invalid">
                      {String(errors.addresses?.[index]?.line2)}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`addresses.${index}.city`)}
                    className={errors.addresses?.[index]?.city ? 'is-invalid' : ''}
                  />
                  {errors.addresses?.[index]?.city && (
                    <Form.Control.Feedback type="invalid">
                      {String(errors.addresses?.[index]?.city)}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`addresses.${index}.province`)}
                    className={errors.addresses?.[index]?.province ? 'is-invalid' : ''}
                  />
                  {errors.addresses?.[index]?.province && (
                    <Form.Control.Feedback type="invalid">
                      {String(errors.addresses?.[index]?.province)}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`addresses.${index}.postalCode`)}
                    className={errors.addresses?.[index]?.postalCode ? 'is-invalid' : ''}
                  />
                  {errors.addresses?.[index]?.postalCode && (
                    <Form.Control.Feedback type="invalid">
                      {String(errors.addresses?.[index]?.postalCode)}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`addresses.${index}.country`)}
                    className={errors.addresses?.[index]?.country ? 'is-invalid' : ''}
                  />
                  {errors.addresses?.[index]?.country && (
                    <Form.Control.Feedback type="invalid">
                      {String(errors.addresses?.[index]?.country)}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      ))}

      {fields.length === 0 && (
        <div className="text-center py-5">
          <i className="fa fa-map-marker text-muted" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3 text-muted">No addresses added yet</p>
          <Button variant="outline-primary" onClick={handleAddAddress}>
            <i className="fa fa-plus me-2"></i>
            Add Address
          </Button>
        </div>
      )}
    </div>
  );
};

export default AddressStep; 